import React, { useState } from 'react';
import './Matrix.css';
import { Button } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

const Matrix = () => {
  const [inputMethod, setInputMethod] = useState('craft'); // 'craft' or 'upload'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [formData, setFormData] = useState({
    projectName: '',
    projectType: '',
    timeline: '',
    budget: '',
    description: '',
    features: '',
    technicalRequirements: '',
    targetAudience: ''
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  return (
    <div className="matrix-page">
      <section className="matrix-hero">
        <div className="container">
          <h1>Project Matrix</h1>
          <p>Create your project proposal or get a quote in minutes</p>
        </div>
      </section>

      <section className="matrix-content">
        <div className="container">
          <div className="method-toggle">
            <button
              className={`toggle-btn Ksh{inputMethod === 'craft' ? 'active' : ''}`}
              onClick={() => setInputMethod('craft')}
            >
              Craft Proposal
            </button>
            <button
              className={`toggle-btn Ksh{inputMethod === 'upload' ? 'active' : ''}`}
              onClick={() => setInputMethod('upload')}
            >
              Upload Document
            </button>
          </div>

          {inputMethod === 'craft' ? (
            <div className="craft-section">
              <div className="info-banner">
                <InfoIcon />
                <div>
                  <strong>How to craft your proposal:</strong>
                  <p>Fill in the details below to help us understand your project requirements. The more information you provide, the more accurate our quote will be.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="matrix-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Project Name *</label>
                    <input
                      type="text"
                      name="projectName"
                      placeholder="e.g., E-commerce Platform"
                      value={formData.projectName}
                      onChange={handleInputChange}
                      required
                    />
                    <span className="field-hint">Give your project a descriptive name</span>
                  </div>

                  <div className="form-group">
                    <label>Project Type *</label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select type...</option>
                      <option value="web">Web Application</option>
                      <option value="mobile">Mobile App (iOS/Android)</option>
                      <option value="desktop">Desktop Software</option>
                      <option value="api">API Development</option>
                      <option value="integration">System Integration</option>
                      <option value="other">Other</option>
                    </select>
                    <span className="field-hint">Choose the primary type of software</span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Timeline *</label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select timeline...</option>
                      <option value="1-2months">1-2 months</option>
                      <option value="3-4months">3-4 months</option>
                      <option value="5-6months">5-6 months</option>
                      <option value="6+months">6+ months</option>
                      <option value="flexible">Flexible</option>
                    </select>
                    <span className="field-hint">Expected project duration</span>
                  </div>

                  <div className="form-group">
                    <label>Budget Range *</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select budget...</option>
                      <option value="5k-10k">Ksh5,000 - Ksh10,000</option>
                      <option value="10k-25k">Ksh10,000 - Ksh25,000</option>
                      <option value="25k-50k">Ksh25,000 - Ksh50,000</option>
                      <option value="50k-100k">Ksh50,000 - Ksh100,000</option>
                      <option value="100k+">Ksh100,000+</option>
                    </select>
                    <span className="field-hint">Your estimated budget</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Project Description *</label>
                  <textarea
                    name="description"
                    placeholder="Describe what you want to build and the problem it solves..."
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    required
                  ></textarea>
                  <span className="field-hint">Provide a clear overview of your project goals and objectives</span>
                </div>

                <div className="form-group">
                  <label>Key Features *</label>
                  <textarea
                    name="features"
                    placeholder="List the main features you need (one per line)&#10;- User authentication&#10;- Payment processing&#10;- Admin dashboard&#10;- etc."
                    value={formData.features}
                    onChange={handleInputChange}
                    rows="6"
                    required
                  ></textarea>
                  <span className="field-hint">List all essential features and functionalities</span>
                </div>

                <div className="form-group">
                  <label>Technical Requirements</label>
                  <textarea
                    name="technicalRequirements"
                    placeholder="Any specific technologies, integrations, or technical constraints?&#10;e.g., Must integrate with Mpesa, Need to support 10,000 concurrent users, etc."
                    value={formData.technicalRequirements}
                    onChange={handleInputChange}
                    rows="4"
                  ></textarea>
                  <span className="field-hint">Specify any technical preferences or requirements (optional)</span>
                </div>

                <div className="form-group">
                  <label>Target Audience</label>
                  <input
                    type="text"
                    name="targetAudience"
                    placeholder="e.g., Small business owners, Healthcare professionals, Students"
                    value={formData.targetAudience}
                    onChange={handleInputChange}
                  />
                  <span className="field-hint">Who will be using this software? (optional)</span>
                </div>

                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  type="submit"
                  fullWidth
                  sx={{
                    backgroundColor: '#2271b1',
                    color: '#ffffff',
                    fontWeight: '600',
                    padding: '14px 32px',
                    fontSize: '15px',
                    textTransform: 'none',
                    marginTop: '24px',
                    borderRadius: '3px',
                    boxShadow: '0 1px 0 #135e96',
                    '&:hover': {
                      backgroundColor: '#135e96'
                    }
                  }}
                >
                  Generate Quote
                </Button>
              </form>
            </div>
          ) : (
            <div className="upload-section">
              <div className="info-banner">
                <InfoIcon />
                <div>
                  <strong>Document Upload Guidelines:</strong>
                  <p>Upload a detailed project proposal, requirements document, or RFP. Accepted formats: PDF, DOC, DOCX, TXT (max 10MB)</p>
                </div>
              </div>

              <div className="upload-requirements">
                <h3>What to include in your document:</h3>
                <ul>
                  <li><strong>Project Overview:</strong> Brief description of what you want to build</li>
                  <li><strong>Objectives:</strong> What problems does this solve? What are your goals?</li>
                  <li><strong>Features & Functionality:</strong> Detailed list of required features</li>
                  <li><strong>Technical Requirements:</strong> Any specific technologies or integrations</li>
                  <li><strong>Timeline:</strong> Expected start date and deadline</li>
                  <li><strong>Budget:</strong> Your budget range or constraints</li>
                  <li><strong>Target Users:</strong> Who will use this software?</li>
                  <li><strong>Design Preferences:</strong> Any UI/UX requirements or examples</li>
                </ul>
              </div>

              <div className="file-upload-container">
                {!uploadedFile ? (
                  <label className="file-upload-area">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <AttachFileIcon sx={{ fontSize: 64, color: '#2271b1', marginBottom: 2 }} />
                    <h3>Drop your document here</h3>
                    <p>or click to browse</p>
                    <span className="file-types">PDF, DOC, DOCX, TXT (max 10MB)</span>
                  </label>
                ) : (
                  <div className="uploaded-file-display">
                    <div className="file-icon">
                      <AttachFileIcon sx={{ fontSize: 48, color: '#2271b1' }} />
                    </div>
                    <div className="file-info">
                      <h4>{uploadedFile.name}</h4>
                      <p>{(uploadedFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={handleRemoveFile}
                      title="Remove file"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                )}
              </div>

              {uploadedFile && (
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  fullWidth
                  sx={{
                    backgroundColor: '#2271b1',
                    color: '#ffffff',
                    fontWeight: '600',
                    padding: '14px 32px',
                    fontSize: '15px',
                    textTransform: 'none',
                    marginTop: '24px',
                    borderRadius: '3px',
                    boxShadow: '0 1px 0 #135e96',
                    '&:hover': {
                      backgroundColor: '#135e96'
                    }
                  }}
                >
                  Submit Document & Get Quote
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Matrix;
